/**
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 Jonathan Rowny
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * https://github.com/jrowny/react-absolute-grid
 *
 */
import { cutoffs } from '../../../constants';

export default class DragManager {
    /* eslint-disable no-undef */
    dragItem;
    dragX;
    dragY;
    initialMouseX;
    initialMouseY;
    initialEventX;
    initialEventY;
    markerProp;
    /* eslint-enable no-undef */

    constructor(moveFn, dragStartFn, dragEndFn, dragMoveFn, markerProp, items) {
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.dragMove = this.dragMove.bind(this);
        this.endDrag = this.endDrag.bind(this);
        this.moveFn = moveFn;
        this.dragStartFn = dragStartFn;
        this.dragEndFn = dragEndFn;
        this.dragMoveFn = dragMoveFn;
        this.markerProp = markerProp;
        this.items = items;
    }

    handleMouseMove(event) {
        const height = window.innerHeight;
        this.stop = true;

        if (event.clientY >= height * cutoffs.SCROLL_ZONE_DOWN) {
            this.stop = false;
            this.handleScroll(1);
        } else if (event.clientY <= height * cutoffs.SCROLL_ZONE_UP) {
            this.stop = false;
            this.handleScroll(-1);
        }
    }

    handleScroll(step) {
        document.body.scrollTop += step;

        if (!this.stop) {
            this.timeout = setTimeout(() => {
                this.handleScroll(step);
            }, cutoffs.SCROLL_TIMEOUT);
        } else {
            clearTimeout(this.timeout);
        }
    }

    dragMove(event) {
        const isTouch = event.touches && event.touches.length;
        const pageX = isTouch ? event.touches[0].pageX : event.pageX;
        const pageY = isTouch ? event.touches[0].pageY : event.pageY;

        const xMovement = Math.abs(this.initialEventX - pageX);
        const yMovement = Math.abs(this.initialEventY - pageY);

        if (xMovement > cutoffs.MOVE_TOLERANCE || yMovement > cutoffs.MOVE_TOLERANCE) {
            const clientX = isTouch ? event.touches[0].clientX : event.clientX;
            const clientY = isTouch ? event.touches[0].clientY : event.clientY;

            this.dragX = clientX - this.initialMouseX;
            this.dragY = clientY - this.initialMouseY;

            const height = window.innerHeight;
            this.stop = true;

            if (clientY >= height * cutoffs.SCROLL_ZONE_DOWN) {
                this.stop = false;
                this.handleScroll(1);
            } else if (clientY <= height * cutoffs.SCROLL_ZONE_UP) {
                this.stop = false;
                this.handleScroll(-1);
            }

            this.update(this.dragX, this.dragY);

            let targetMarker;
            let targetElement = document.elementFromPoint(clientX, clientY);
            while (targetElement && targetElement.parentNode) {
                if (targetElement.getAttribute('data-marker')) {
                    targetMarker = targetElement.getAttribute('data-marker');
                    break;
                }
                targetElement = targetElement.parentNode;
            }

            if (targetMarker) {
                this.targetMarker = targetMarker;
                this.moveFn(this.dragItem[this.markerProp], targetMarker);
            }

            event.stopPropagation();
            event.preventDefault();
        }

        this.dragMoveFn(event);
    }

    endDrag(startIndex, endIndex) {
        document.removeEventListener('mousemove', this.dragMove);
        document.removeEventListener('mouseup', this.endDrag);
        if (typeof startIndex !== 'undefined' && typeof endIndex !== 'undefined') {
            this.dragEndFn(startIndex, endIndex);
        }
        this.dragItem = null;
        if (this.update && typeof this.update === 'function') {
            this.update(null, null);
        }
        this.update = null;
    }

    startDrag(event, domNode, item, fnUpdate, fnDragEnd) {
        const isTouch = event.targetTouches && event.targetTouches.length === 1;
        if (event.button === 0 || isTouch) {
            const rect = domNode.getBoundingClientRect();

            this.update = fnUpdate;
            this.dragItem = item;
            const pageX = isTouch ? event.targetTouches[0].pageX : event.pageX;
            const pageY = isTouch ? event.targetTouches[0].pageY : event.pageY;

            this.initialMouseX = Math.round(pageX - (rect.left + window.pageXOffset));
            this.initialMouseY = Math.round(pageY - (rect.top + window.pageYOffset));
            this.initialEventX = pageX;
            this.initialEventY = pageY;

            this.update(event.clientX - this.initialMouseX, event.clientY - this.initialMouseY);

            document.addEventListener('mousemove', this.dragMove);
            document.addEventListener('touchmove', this.dragMove);
            document.addEventListener('mouseup', fnDragEnd);
            document.addEventListener('touchend', fnDragEnd);
            document.addEventListener('touchcancel', fnDragEnd);

            event.preventDefault();
        }

        this.dragStartFn(event);
    }

    getStyle(x, y) {
        const transform = `translate3d(${x}px, ${y}px, 0)`;
        return {
            left: 0,
            MozTransform: transform,
            MozTransition: 'none',
            msTransform: transform,
            msTransition: 'none',
            pointerEvents: 'none',
            position: 'fixed',
            top: 0,
            transform: transform,
            transition: 'none',
            WebkitTransform: transform,
            WebkitTransition: 'none',
            zIndex: 1000,
        };
    }
}
